import { ApplicationRef, createComponent, EnvironmentInjector } from "@angular/core";
import { Editor } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import { PluginKey } from "@tiptap/pm/state";
import { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";
import { initialsOf, UserAvatarComponent } from "src/app/components/user-avatar/user-avatar.component";

export interface MentionableUser {
  emailAddress: string;
  displayName: string;
}

interface MentionItem {
  id: string;
  label: string;
}

const MAX_SUGGESTIONS = 8;
const AVATAR_SIZE = 24;

interface MountedAvatar {
  element: HTMLElement;
  destroy: () => void;
}

// The popup is plain DOM, outside any Angular template, so the avatar is mounted by hand to share
// the app's avatar and its photo cache.
function mountAvatar(appRef: ApplicationRef, injector: EnvironmentInjector, item: MentionItem): MountedAvatar {
  const ref = createComponent(UserAvatarComponent, { environmentInjector: injector });

  ref.setInput("userEmail", item.id);
  ref.setInput("initials", initialsOf(item.label));
  ref.setInput("size", AVATAR_SIZE);
  appRef.attachView(ref.hostView);
  ref.changeDetectorRef.detectChanges();

  return {
    element: ref.location.nativeElement,
    destroy: () => {
      appRef.detachView(ref.hostView);
      ref.destroy();
    },
  };
}

export const userMentionPluginKey = new PluginKey("userMention");

export function isMentionListOpen(editor: Editor): boolean {
  return !!userMentionPluginKey.getState(editor.state)?.active;
}

// The backend reads data-id as the mentioned user's email, so id must stay the address.
export function createUserMention(
  getUsers: () => MentionableUser[],
  appRef: ApplicationRef,
  injector: EnvironmentInjector,
) {
  return Mention.configure({
    // Angular's [innerHTML] sanitiser strips data-* attributes, so the class is what styles a rendered mention.
    HTMLAttributes: { class: "mention" },
    suggestion: {
      char: "@",
      pluginKey: userMentionPluginKey,
      items: ({ query }): MentionItem[] => {
        const q = query.toLowerCase();
        return getUsers()
          .filter(
            (u) =>
              u.emailAddress &&
              (u.displayName?.toLowerCase().includes(q) ||
                u.emailAddress.toLowerCase().includes(q)),
          )
          .slice(0, MAX_SUGGESTIONS)
          .map((u) => ({ id: u.emailAddress, label: u.displayName || u.emailAddress }));
      },
      render: () => new MentionSuggestionPopup(appRef, injector),
    },
  });
}

class MentionSuggestionPopup {
  private element: HTMLDivElement | null = null;
  private props: SuggestionProps<MentionItem> | null = null;
  private selectedIndex = 0;
  private avatars: MountedAvatar[] = [];

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
  ) {}

  onStart = (props: SuggestionProps<MentionItem>) => {
    this.element = document.createElement("div");
    this.element.className = "mention-suggestions";
    document.body.appendChild(this.element);
    this.update(props);
  };

  onUpdate = (props: SuggestionProps<MentionItem>) => this.update(props);

  onKeyDown = ({ event }: SuggestionKeyDownProps): boolean => {
    const count = this.props?.items.length ?? 0;

    if (event.key === "Escape") {
      this.destroy();
      return true;
    }
    if (count === 0) return false;

    if (event.key === "ArrowDown") {
      this.selectedIndex = (this.selectedIndex + 1) % count;
      this.highlight();
      return true;
    }
    if (event.key === "ArrowUp") {
      this.selectedIndex = (this.selectedIndex + count - 1) % count;
      this.highlight();
      return true;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      this.select(this.selectedIndex);
      return true;
    }
    return false;
  };

  onExit = () => this.destroy();

  private update(props: SuggestionProps<MentionItem>) {
    this.props = props;
    this.selectedIndex = 0;
    this.renderItems();
    this.position();
  }

  private select(index: number) {
    const item = this.props?.items[index];
    if (item) this.props.command(item);
  }

  private renderItems() {
    if (!this.element || !this.props) return;

    this.destroyAvatars();
    this.element.replaceChildren();

    if (this.props.items.length === 0) {
      const empty = document.createElement("div");
      empty.className = "mention-suggestions__empty";
      empty.textContent = $localize`:@@mentionNoUsersFound:No users found`;
      this.element.appendChild(empty);
      return;
    }

    this.props.items.forEach((item, index) => {
      const option = document.createElement("button");
      option.type = "button";
      option.className =
        "mention-suggestions__item" + (index === this.selectedIndex ? " is-selected" : "");
      // mousedown, not click: a click would blur the editor and end the suggestion first.
      option.addEventListener("mousedown", (event) => {
        event.preventDefault();
        this.select(index);
      });

      option.title = item.id;

      const avatar = mountAvatar(this.appRef, this.injector, item);
      this.avatars.push(avatar);

      const name = document.createElement("span");
      name.className = "mention-suggestions__name";
      name.textContent = item.label;

      option.append(avatar.element, name);
      this.element.appendChild(option);
    });
  }

  private highlight() {
    this.element
      ?.querySelectorAll(".mention-suggestions__item")
      .forEach((option, index) => option.classList.toggle("is-selected", index === this.selectedIndex));
  }

  private destroyAvatars() {
    this.avatars.forEach((avatar) => avatar.destroy());
    this.avatars = [];
  }

  private position() {
    const rect = this.props?.clientRect?.();
    if (!this.element || !rect) return;

    this.element.style.left = `${rect.left + window.scrollX}px`;
    this.element.style.top = `${rect.bottom + window.scrollY + 4}px`;
  }

  private destroy() {
    this.destroyAvatars();
    this.element?.remove();
    this.element = null;
    this.props = null;
  }
}
